import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@material/web/button/text-button.js";
import "@material/web/icon/icon.js";

import { AuthController } from "../lib/auth-controller";
import { getOrganization } from "../lib/org-store";
import { logout, hasRole } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { icon } from "../lib/icons";
import { getNavCounts, formatCount, type NavCounts } from "../lib/nav-counts";
import "./material/dpp-top-app-bar";
import type { Organization } from "../lib/types";

interface NavItem {
  href: string;
  label: string;
  icon: string;
  /** Which live count badge this destination shows, if any. */
  count?: "products" | "tags";
  /** A specialized surface, shown only when the org actually uses it
   * (provisioned at onboarding per industry/product). Core items omit this. */
  requires?: "giAuthorized" | "usesNfc";
  /** Shown only to holders of this role (e.g. the cross-tenant inspector). */
  role?: string;
}

interface NavGroup {
  /** Tier heading; omitted for the top overview group. */
  header?: string;
  items: NavItem[];
}

/**
 * Navigation reads as the product's capability hierarchy, most-standalone
 * first: ANTI-COUNTERFEIT (the flagship — no regulation) → GEOGRAPHICAL
 * INDICATION (some compliance) → DIGITAL PRODUCT PASSPORT (regulatory). The
 * three layers are independent and composable: a product can carry any of them,
 * and a passport is added ON TOP of a protected or authenticated product, never
 * required by it. Products + Dashboard sit above the tiers as the shared base
 * everything attaches to; Account sits below.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { href: "/", label: "Dashboard", icon: "dashboard" },
      { href: "/products", label: "Products", icon: "inventory_2", count: "products" },
    ],
  },
  {
    header: "Anti-counterfeit",
    items: [
      { href: "/nfc", label: "NFC tags", icon: "nfc", count: "tags", requires: "usesNfc" },
      { href: "/inspection", label: "Inspection", icon: "policy", role: "inspector" },
    ],
  },
  {
    header: "Geographical Indication",
    items: [
      { href: "/gi", label: "GI registry", icon: "workspace_premium", requires: "giAuthorized" },
    ],
  },
  {
    header: "Digital Product Passport",
    items: [
      { href: "/recall", label: "Recalls", icon: "campaign" },
      { href: "/assistance", label: "Assistance", icon: "volunteer_activism" },
      { href: "/ingest", label: "Passport data", icon: "document_scanner" },
    ],
  },
  {
    header: "Account",
    items: [
      { href: "/members", label: "Team", icon: "groups" },
      { href: "/security", label: "Security", icon: "key" },
      { href: "/audit", label: "Audit log", icon: "receipt_long" },
    ],
  },
];

/** Persistent console chrome. Mobile-first (design system §6): Compact
 * (<840px) gets a sticky top app bar with a menu button opening a modal
 * navigation drawer (M3 drawer spec — 56px destinations, labels + counts,
 * scrim); Medium+ keeps the persistent 248px rail with the page content on a
 * rounded surface "sheet" (admin-console design). */
@customElement("app-shell")
export class AppShell extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      min-height: 100dvh;
    }

    .shell {
      min-height: 100vh;
      min-height: 100dvh;
    }

    md-icon svg {
      width: 1em;
      height: 1em;
      fill: currentColor;
    }

    /* Compact: sticky top app bar carries brand + menu + sign out. */
    .mobile-bar {
      display: block;
      position: sticky;
      top: 0;
      z-index: 10;
      border-bottom: 1px solid var(--md-sys-color-outline-variant);
      padding-top: env(safe-area-inset-top, 0px);
      background: var(--md-sys-color-surface-container);
    }

    .bar-brand {
      display: inline-flex;
      color: var(--md-sys-color-primary);
      --md-icon-size: 24px;
    }

    .menu-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border: none;
      border-radius: 999px;
      background: transparent;
      color: var(--md-sys-color-on-surface);
      cursor: pointer;
      --md-icon-size: 24px;
    }

    .menu-btn:active {
      background: color-mix(
        in srgb,
        var(--md-sys-color-on-surface) 10%,
        transparent
      );
    }

    /* Compact: the nav is a modal drawer off-canvas at the inline start. */
    .nav {
      position: fixed;
      inset-block: 0;
      inset-inline-start: 0;
      z-index: 30;
      width: min(304px, 85vw);
      display: flex;
      flex-direction: column;
      padding: var(--space-4, 16px) var(--space-3, 12px);
      padding-inline-start: calc(
        var(--space-3, 12px) + env(safe-area-inset-left, 0px)
      );
      box-sizing: border-box;
      gap: 2px;
      overflow-y: auto;
      background: var(--md-sys-color-surface-container-high);
      border-start-end-radius: 16px;
      border-end-end-radius: 16px;
      transform: translateX(-105%);
      visibility: hidden;
      transition:
        transform 0.2s ease,
        visibility 0.2s;
    }

    :host([dir="rtl"]) .nav,
    :dir(rtl) .nav {
      transform: translateX(105%);
    }

    .nav--open {
      transform: none;
      visibility: visible;
    }

    .scrim {
      position: fixed;
      inset: 0;
      z-index: 20;
      background: var(--md-sys-color-scrim, #000);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
      border: none;
      padding: 0;
    }

    .scrim--open {
      opacity: 0.4;
      pointer-events: auto;
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px 20px;
      font: 400 20px/1 var(--font-brand, Roboto, sans-serif);
      color: var(--md-sys-color-on-surface);
    }

    .brand__icon {
      display: inline-flex;
      color: var(--md-sys-color-primary);
      --md-icon-size: 24px;
    }

    nav {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .nav__group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .nav__group + .nav__group {
      margin-top: 14px;
    }

    /* Tier label — the anti-counterfeit → GI → DPP hierarchy made visible. */
    .nav__group-header {
      padding: 6px 16px 4px;
      font: 600 11px/1.3 var(--font-plain, sans-serif);
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: var(--md-sys-color-on-surface-variant);
      opacity: 0.7;
    }

    .nav__item {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 56px;
      padding: 0 16px;
      border-radius: 999px;
      text-decoration: none;
      color: var(--md-sys-color-on-surface-variant);
      font: 500 14px/1 var(--font-plain, sans-serif);
      transition: background-color 0.15s ease;
    }

    .nav__item:hover:not(.nav__item--active),
    .nav__item:focus-visible:not(.nav__item--active) {
      background: color-mix(
        in srgb,
        var(--md-sys-color-on-surface) 8%,
        transparent
      );
    }

    .nav__item:focus-visible,
    .menu-btn:focus-visible,
    .scrim:focus-visible {
      outline: 2px solid var(--md-sys-color-primary);
      outline-offset: 2px;
    }

    .nav__item--active {
      background: var(--md-sys-color-secondary-container);
      color: var(--md-sys-color-on-secondary-container);
    }

    .nav__icon {
      display: inline-flex;
      --md-icon-size: 22px;
    }

    .nav__count {
      margin-left: auto;
      font: 500 12px/1 var(--font-plain, sans-serif);
    }

    .nav__footer {
      margin-top: auto;
      padding-top: 16px;
      padding-bottom: env(safe-area-inset-bottom, 0px);
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .org-name {
      font: 500 13px/1.3 var(--font-plain, sans-serif);
      padding: 0 16px;
      overflow-wrap: break-word;
    }

    .user {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 0 4px 0 16px;
    }

    .user__name {
      font-size: 0.8125rem;
      color: var(--md-sys-color-on-surface-variant);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    .content {
      min-width: 0;
      background: var(--md-sys-color-surface);
    }

    /* Medium+ (≥840px): persistent rail, drawer chrome hidden, content is a
       rounded surface sheet that scrolls internally. */
    @media (min-width: 840px) {
      :host {
        height: 100vh;
        height: 100dvh;
      }

      .shell {
        display: flex;
        height: 100%;
      }

      .mobile-bar,
      .scrim {
        display: none;
      }

      .nav {
        position: static;
        z-index: auto;
        width: 248px;
        flex: none;
        transform: none;
        visibility: visible;
        background: transparent;
        border-radius: 0;
        transition: none;
      }

      .content {
        flex: 1;
        overflow-y: auto;
        border-start-start-radius: 16px;
        border-end-start-radius: 16px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .nav,
      .scrim,
      .nav__item {
        transition: none;
      }
    }
  `;

  @property({ type: String }) active = "";

  @state() private org: Organization | null = null;

  @state() private counts: NavCounts | null = null;

  @state() private capsLoaded = false;

  @state() private drawerOpen = false;

  /** Medium+ shows the persistent rail; the drawer only exists on Compact. */
  private wideQuery = window.matchMedia("(min-width: 840px)");

  @state() private isWide = this.wideQuery.matches;

  private auth = new AuthController(this);

  private onWideChange = (e: MediaQueryListEvent): void => {
    this.isWide = e.matches;
    if (e.matches) this.drawerOpen = false;
  };

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.drawerOpen) this.closeDrawer();
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.wideQuery.addEventListener("change", this.onWideChange);
    window.addEventListener("keydown", this.onKeydown);
    void this.loadOrg();
    void getNavCounts().then((counts) => {
      this.counts = counts;
      this.capsLoaded = true;
    });
  }

  disconnectedCallback(): void {
    this.wideQuery.removeEventListener("change", this.onWideChange);
    window.removeEventListener("keydown", this.onKeydown);
    super.disconnectedCallback();
  }

  private toggleDrawer = (): void => {
    this.drawerOpen = !this.drawerOpen;
  };

  private closeDrawer = (): void => {
    this.drawerOpen = false;
  };

  /** Nav items visible to this org: core always; specialized surfaces (GI, NFC)
   * only once we know the org uses them. Hidden while capabilities load, and
   * fail-open (shown) if the probe errored — never hide a feature on a glitch. */
  private showItem(item: NavItem): boolean {
    if (item.role && !hasRole(item.role)) return false;
    if (!item.requires) return true;
    if (!this.capsLoaded) return false;
    if (!this.counts) return true;
    return this.counts[item.requires] === true;
  }

  /** Visible tiers: each group's items filtered, then empty groups dropped so a
   * tier header never sits alone (e.g. GI hidden for a non-GI org). */
  private navGroups(): NavGroup[] {
    return NAV_GROUPS.map((g) => ({
      header: g.header,
      items: g.items.filter((item) => this.showItem(item)),
    })).filter((g) => g.items.length > 0);
  }

  private async loadOrg(): Promise<void> {
    try {
      this.org = await getOrganization();
    } catch {
      this.org = null;
    }
  }

  private handleLogout = (): void => {
    logout();
    void navigate("/login");
  };

  private countFor(item: NavItem): string | null {
    if (!item.count || !this.counts) return null;
    const n = this.counts[item.count];
    return n > 0 ? formatCount(n) : null;
  }

  render() {
    const user = this.auth.user;
    const drawerHidden = !this.isWide && !this.drawerOpen;
    return html`
      <dpp-top-app-bar class="mobile-bar" headline="Authentrace">
        <button
          slot="leading"
          class="menu-btn"
          aria-label="Open navigation menu"
          aria-expanded=${this.drawerOpen ? "true" : "false"}
          @click=${this.toggleDrawer}
        >
          ${icon("menu")}
        </button>
        <span slot="leading" class="bar-brand">${icon("fingerprint")}</span>
        <md-text-button slot="trailing" @click=${this.handleLogout}>
          Sign out
        </md-text-button>
      </dpp-top-app-bar>
      <div class="shell">
        <button
          class="scrim ${this.drawerOpen ? "scrim--open" : ""}"
          aria-label="Close navigation menu"
          tabindex=${this.drawerOpen ? "0" : "-1"}
          @click=${this.closeDrawer}
        ></button>
        <aside
          class="nav ${this.drawerOpen ? "nav--open" : ""}"
          ?inert=${drawerHidden}
          aria-label="Main navigation"
        >
          <div class="brand">
            <span class="brand__icon">${icon("fingerprint")}</span>
            <span>Authentrace</span>
          </div>
          <nav>
            ${this.navGroups().map(
              (group) => html`
                <div class="nav__group">
                  ${group.header
                    ? html`<div class="nav__group-header">
                        ${group.header}
                      </div>`
                    : null}
                  ${group.items.map((item) => {
                    const count = this.countFor(item);
                    return html`
                      <a
                        href=${item.href}
                        aria-label=${item.label}
                        aria-current=${this.active === item.href
                          ? "page"
                          : "false"}
                        class="nav__item ${this.active === item.href
                          ? "nav__item--active"
                          : ""}"
                        @click=${this.closeDrawer}
                      >
                        <span class="nav__icon">${icon(item.icon)}</span>
                        <span class="nav__label">${item.label}</span>
                        ${count
                          ? html`<span class="nav__count">${count}</span>`
                          : null}
                      </a>
                    `;
                  })}
                </div>
              `,
            )}
          </nav>
          <div class="nav__footer">
            ${this.org
              ? html`<div class="org-name">${this.org.Name}</div>`
              : null}
            <div class="user">
              <span class="user__name"
                >${user?.display_name || user?.email}</span
              >
              <md-text-button @click=${this.handleLogout}>
                Sign out
              </md-text-button>
            </div>
          </div>
        </aside>
        <main class="content">
          <slot></slot>
        </main>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-shell": AppShell;
  }
}
